import React from 'react';
import { useOptionalTheme, type SkyInputs } from '../../contexts/ThemeContext';
import { containingWindow, sunTimes } from '../../utils/sun';
import { h23Hour } from '../../utils/zonedInstant';
import './DaylightLine.css';

/**
 * Today's daylight line (Almanac S3, D6): a thin sky ramp from an hour before
 * sunrise to an hour after sunset, an ink marker at now, and the two times.
 *
 * The times are S2's `sunTimes(now)` — the solar cycle nearest `now` at the sun
 * location, the same location the surface reads (decision 5): inside a trip
 * window, that window's destination; otherwise home. They carry no calendar
 * day. Inside a window they are shown on the plan's clock (`effective_timezone`,
 * `zoneClock`); at home on the user's own clock (`deviceClock`, the one
 * device-zone reader here — local getters, no zone argument). No location, or a
 * polar cycle: no line.
 */

/** The ramp's key colours, in order; each is a `--sky-*` token in tokens.css. */
export type SkyKey = 'night' | 'blue' | 'rise' | 'morn' | 'day' | 'gold' | 'set' | 'dusk';

export interface RampStop {
  key: SkyKey;
  /** Position along the line, 0 (start) to 1 (end), never decreasing. */
  at: number;
}

const MINUTE_MS = 60 * 1000;
/** The line runs from sunrise − 70 min to sunset + 70 min (night at both ends). */
export const LINE_MARGIN_MS = 70 * MINUTE_MS;

/**
 * The ramp for one cycle, as the design lays it out: night until 70 min before
 * sunrise, blue hour at −30, sunrise, morning at +80, midday at solar noon (the
 * midpoint), morning again at sunset − 90, golden hour at −40, sunset, dusk at
 * +30, night at +70. Positions are clamped to the line and made monotonic, so a
 * short day's overlapping stops collapse rather than run backwards.
 */
export function rampStops(sunrise: Date, sunset: Date): RampStop[] {
  const r = sunrise.getTime();
  const s = sunset.getTime();
  const t0 = r - LINE_MARGIN_MS;
  const t1 = s + LINE_MARGIN_MS;
  const span = t1 - t0;
  const raw: Array<[SkyKey, number]> = [
    ['night', t0],
    ['blue', r - 30 * MINUTE_MS],
    ['rise', r],
    ['morn', r + 80 * MINUTE_MS],
    ['day', (r + s) / 2],
    ['morn', s - 90 * MINUTE_MS],
    ['gold', s - 40 * MINUTE_MS],
    ['set', s],
    ['dusk', s + 30 * MINUTE_MS],
    ['night', t1],
  ];
  let floor = 0;
  return raw.map(([key, t]) => {
    const at = Math.max(floor, Math.min(1, Math.max(0, (t - t0) / span)));
    floor = at;
    return { key, at };
  });
}

/** Where `now` sits on the line, 0 to 1, clamped at both ends. */
export function nowPosition(now: Date, sunrise: Date, sunset: Date): number {
  const t0 = sunrise.getTime() - LINE_MARGIN_MS;
  const t1 = sunset.getTime() + LINE_MARGIN_MS;
  return Math.min(1, Math.max(0, (now.getTime() - t0) / (t1 - t0)));
}

interface Clock {
  hour: number;
  minute: number;
}

/** The wall clock `zone` shows at `d`, or `null` when the zone is unreadable. */
export function zoneClock(d: Date, zone: string): Clock | null {
  try {
    const parts: Record<string, string> = {};
    new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', hour: 'numeric', minute: 'numeric' })
      .formatToParts(d)
      .forEach(({ type, value }) => {
        parts[type] = value;
      });
    // A 12-hour read (an engine that ignored h23) fails closed: no line
    // rather than a wrong time. The rule is the reader's own.
    const hour = h23Hour(parts);
    const minute = Number(parts.minute);
    return hour === null || Number.isNaN(minute) ? null : { hour, minute };
  } catch {
    return null;
  }
}

/** The user's own clock at `d`: the device zone, through local getters. */
export function deviceClock(d: Date): Clock {
  return { hour: d.getHours(), minute: d.getMinutes() };
}

/** `7:02` / `7:04 pm`: sunrise without its (morning) suffix, sunset with it. */
export function formatClock({ hour, minute }: Clock, withSuffix: boolean): string {
  const h12 = hour % 12 || 12;
  const text = `${h12}:${minute < 10 ? '0' : ''}${minute}`;
  if (withSuffix || hour >= 12) return `${text} ${hour < 12 ? 'am' : 'pm'}`;
  return text;
}

export interface DaylightModel {
  stops: RampStop[];
  now: number;
  sunrise: string;
  sunset: string;
}

/** The line to draw for these inputs, or `null` when there is none. */
export function daylightModel({ now, home, windows }: SkyInputs): DaylightModel | null {
  const w = containingWindow(windows, now);
  const at = w && w.destination_lat != null && w.destination_lng != null
    ? { lat: w.destination_lat, lng: w.destination_lng }
    : home;
  if (!at) return null;
  const times = sunTimes(now, at.lat, at.lng);
  if (!times) return null;
  const clockOf = (d: Date) => (w ? zoneClock(d, w.effective_timezone) : deviceClock(d));
  const rise = clockOf(times.sunrise);
  const set = clockOf(times.sunset);
  if (!rise || !set) return null;
  return {
    stops: rampStops(times.sunrise, times.sunset),
    now: nowPosition(now, times.sunrise, times.sunset),
    sunrise: formatClock(rise, false),
    sunset: formatClock(set, true),
  };
}

const pct = (x: number) => `${(x * 100).toFixed(2)}%`;

export const DaylightLine: React.FC = () => {
  const theme = useOptionalTheme();
  const model = theme ? daylightModel(theme.sky) : null;
  if (!model) return null;
  const gradient = `linear-gradient(90deg, ${model.stops
    .map((s) => `var(--sky-${s.key}) ${pct(s.at)}`)
    .join(', ')})`;
  return (
    <div className="daylight-line" data-testid="daylight-line">
      <div className="daylight-line__sky" style={{ ['--daylight-ramp' as string]: gradient } as React.CSSProperties} aria-hidden="true">
        <span className="daylight-line__now" style={{ left: pct(model.now) }} />
      </div>
      <div className="daylight-line__times">
        <span>Sunrise {model.sunrise}</span>
        <span>Sunset {model.sunset}</span>
      </div>
    </div>
  );
};

export default DaylightLine;
